import {
  effectiveTierValueType,
  needsCouponDiscount,
  needsOfferCopyLine,
  needsTextDiscount,
  needsTierRules,
} from '@/components/ui/OfferProductPreview';
import type {
  BrandOffer,
  BrandOfferFormData,
  DiscountConfig,
  OfferType,
  TierRule,
} from '@/types/BrandOffer.types';
import { normalizeOfferType } from '../BrandOffersPage.constants';

function numBrandField(v: unknown, fallback = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/** Universal `isSale` / `showTimer` defaults per offer type. SALE_EXTRA_OFFERS
 *  is the only timed mechanic that exposes a countdown toggle; SALE keeps its
 *  days-left caption without a per-second timer. */
export const defaultsForType = (
  type: OfferType
): { isSale: boolean; showTimer: boolean } => {
  if (type === 'SALE_EXTRA_OFFERS') {
    return { isSale: true, showTimer: true };
  }
  if (type === 'SALE') {
    return { isSale: true, showTimer: false };
  }
  return { isSale: false, showTimer: false };
};

/** Countdown timer toggle is only exposed for the sale + extra type. */
export const allowsShowTimerToggle = (type: OfferType): boolean =>
  type === 'SALE_EXTRA_OFFERS';

/** Strip the symbol that decorates the first number in the text (₹ before digits, or % after digits). */
export function stripSuffixSymbol(
  text: string,
  type: 'FLAT' | 'PERCENT'
): string {
  if (!text) return '';
  if (type === 'FLAT') return text.replace(/₹(\d)/, '$1');
  return text.replace(/(\d)%/, '$1');
}

/** Decorate the first numeric token in the text with the type's symbol. Empty bodies and text without numbers stay unchanged. */
export function attachSuffixSymbol(
  body: string,
  type: 'FLAT' | 'PERCENT'
): string {
  if (!body) return '';
  const numberRe = /\d+(?:\.\d+)?/;
  if (!numberRe.test(body)) return body;
  return type === 'FLAT'
    ? body.replace(numberRe, '₹$&')
    : body.replace(numberRe, '$&%');
}

/** Re-decorate offerText when the FLAT/PERCENT pill toggles. */
export function swapSuffixSymbol(
  text: string,
  prevType: 'FLAT' | 'PERCENT' | undefined,
  nextType: 'FLAT' | 'PERCENT'
): string {
  const body =
    prevType === 'FLAT' || prevType === 'PERCENT'
      ? stripSuffixSymbol(text, prevType)
      : text;
  return attachSuffixSymbol(body, nextType);
}

/** Sale and Sale + extra always use the sale pill; `isSale` is forced on. */
export const requiresOnSale = (type: OfferType): boolean =>
  type === 'SALE' || type === 'SALE_EXTRA_OFFERS';

export const buildDefault = (): BrandOfferFormData => {
  const defaults = defaultsForType('EVERYDAY_COUPONS');
  return {
    offerType: 'EVERYDAY_COUPONS',
    brand: null,
    name: '',
    isActive: true,
    isSale: defaults.isSale,
    showTimer: defaults.showTimer,
    priority: 0,
    startAt: null,
    endsAt: null,
    discountConfig: { type: 'TEXT', value: null, offerText: '' },
  };
};

export const offerToForm = (offer: BrandOffer): BrandOfferFormData => {
  const offerType = normalizeOfferType(String(offer.offerType));
  const timerAllowed = allowsShowTimerToggle(offerType);
  const saleLocked = requiresOnSale(offerType);
  return {
    offerType,
    brand: offer.brand,
    name: offer.name,
    isActive: offer.isActive,
    isSale: saleLocked ? true : (offer.isSale ?? false),
    showTimer: timerAllowed ? (offer.showTimer ?? false) : false,
    priority: offer.priority,
    startAt: offer.startAt
      ? new Date(offer.startAt).toISOString().slice(0, 16)
      : null,
    endsAt: offer.endsAt
      ? new Date(offer.endsAt).toISOString().slice(0, 16)
      : null,
    discountConfig: offer.discountConfig,
  };
};

/** Pre-fill a new offer from an existing row (create flow). */
export function formDataFromDuplicateSource(
  source: BrandOffer
): BrandOfferFormData {
  const base = offerToForm(source);
  const nameTrim = base.name.trim();
  return {
    ...base,
    name: nameTrim ? `${nameTrim} (copy)` : 'Copy',
  };
}

/** Initial discount config for a given offer type, or null when not needed. */
export const initialDiscountForType = (
  type: OfferType
): DiscountConfig | null => {
  if (needsTierRules(type)) {
    return {
      type: 'TIER',
      rules: [{ min: 0, max: null, value: 0, tierValueType: 'FLAT' }],
      offerText: '',
    };
  }
  if (needsTextDiscount(type)) {
    return { type: 'TEXT', value: null, offerText: '' };
  }
  if (needsCouponDiscount(type)) {
    return { type: 'FLAT', value: null, offerText: '' };
  }
  return null;
};

export function validate(d: BrandOfferFormData): string[] {
  const errs: string[] = [];

  if (!d.name.trim()) {
    errs.push('Give this offer a short name.');
  }
  if (d.brand == null) {
    errs.push('Pick a brand for this offer.');
  }
  if (Number.isNaN(Number(d.priority))) {
    errs.push('Rank has to be a number.');
  }
  if (!d.endsAt) {
    errs.push('Pick an end date and time for this offer.');
  }
  if (d.startAt && d.endsAt) {
    const startMs = new Date(d.startAt).getTime();
    const endMs = new Date(d.endsAt).getTime();
    if (
      Number.isFinite(startMs) &&
      Number.isFinite(endMs) &&
      startMs >= endMs
    ) {
      errs.push('Start must be before end.');
    }
  }
  if (requiresOnSale(d.offerType) && !d.isSale) {
    errs.push('Sale and sale + extra offers must keep the sale pill on.');
  }

  if (needsCouponDiscount(d.offerType)) {
    if (!d.discountConfig?.type) errs.push('Choose flat rupees or percent.');
    const val = Number(d.discountConfig?.value);
    if (!val || val <= 0)
      errs.push('Enter a discount amount greater than zero.');
    if (d.discountConfig?.type === 'PERCENT' && val > 100) {
      errs.push('Percent cannot go past 100.');
    }
  }
  if (needsTextDiscount(d.offerType)) {
    if (!String(d.discountConfig?.offerText ?? '').trim()) {
      errs.push('Add the line shoppers should read on the tile.');
    }
  }
  if (needsOfferCopyLine(d.offerType)) {
    if (!String(d.discountConfig?.offerText ?? '').trim()) {
      errs.push(
        'Add the suffix copy that appears after "Get at ₹X" on the tile.'
      );
    }
  }
  if (needsTierRules(d.offerType)) {
    const rules = d.discountConfig?.rules ?? [];
    if (rules.length === 0) errs.push('Add at least one cart tier.');
    rules.forEach((r, i) => {
      if (r.min < 0)
        errs.push(`Tier ${i + 1}: minimum cart cannot be negative.`);
      if (r.max !== null && r.max <= r.min) {
        errs.push(
          `Tier ${i + 1}: the high bound must sit above the low bound.`
        );
      }
      if (r.value <= 0) {
        const kind =
          effectiveTierValueType(r, d.offerType) === 'PERCENT'
            ? '% off'
            : 'flat ₹ off';
        errs.push(`Tier ${i + 1}: enter ${kind} above zero.`);
      }
      const kind = effectiveTierValueType(r, d.offerType);
      if (kind === 'PERCENT' && r.value > 100) {
        errs.push(`Tier ${i + 1}: percent cannot exceed 100.`);
      }
    });
  }

  return errs;
}

export function normalizeTierRule(raw: unknown): TierRule {
  if (!raw || typeof raw !== 'object') {
    return { min: 0, max: null, value: 0, tierValueType: 'FLAT' };
  }
  const r = raw as Record<string, unknown>;
  const tierRaw =
    r.tierValueType ?? r.tier_value_type ?? r.discountType ?? r.discount_type;
  let tierValueType: 'FLAT' | 'PERCENT' | undefined;
  if (tierRaw === 'FLAT' || tierRaw === 'PERCENT') {
    tierValueType = tierRaw;
  }

  let max =
    r.max === null || r.max === undefined || r.max === ''
      ? null
      : Number(r.max);
  if (max === 9999999) max = null;

  const base: TierRule = {
    min: numBrandField(r.min),
    max,
    value: numBrandField(r.value),
  };
  if (tierValueType) base.tierValueType = tierValueType;
  return base;
}

export function normalizeDiscountConfigFromApi(
  raw: DiscountConfig | null | undefined
): DiscountConfig | null {
  if (raw == null || typeof raw !== 'object') return raw ?? null;
  if (!Array.isArray(raw.rules) || raw.rules.length === 0) return raw;
  return {
    ...raw,
    rules: raw.rules.map((rule) => normalizeTierRule(rule)),
  };
}

export function normalizeBrandOfferRecord(raw: unknown): BrandOffer {
  const record =
    raw != null && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {};
  const brandRaw = record.brand ?? record.brand_id;
  const startRaw =
    record.startAt ?? record.start_at ?? record.startsAt ?? record.starts_at;
  const endsRaw = record.endsAt ?? record.ends_at;
  const createdRaw = record.createdAt ?? record.created_at;
  const updatedRaw = record.updatedAt ?? record.updated_at;
  const discountRaw =
    record.discountConfig ?? record.discount_config ?? undefined;

  return {
    id: numBrandField(record.id),
    offerType: normalizeOfferType(
      String(record.offerType ?? record.offer_type ?? 'EVERYDAY_COUPONS')
    ),
    brand:
      brandRaw !== undefined && brandRaw !== null && brandRaw !== ''
        ? Number(brandRaw)
        : null,
    name: String(record.name ?? ''),
    priority: numBrandField(record.priority, 0),
    isActive: Boolean(record.isActive ?? record.is_active),
    isSale: Boolean(record.isSale ?? record.is_sale),
    showTimer: Boolean(record.showTimer ?? record.show_timer),
    startAt: startRaw != null && startRaw !== '' ? String(startRaw) : null,
    endsAt: endsRaw != null && endsRaw !== '' ? String(endsRaw) : null,
    discountConfig: normalizeDiscountConfigFromApi(
      discountRaw as BrandOffer['discountConfig']
    ),
    createdAt:
      createdRaw != null && createdRaw !== '' ? String(createdRaw) : null,
    updatedAt:
      updatedRaw != null && updatedRaw !== '' ? String(updatedRaw) : null,
  };
}
